/**
 * ROI calculation logic for the SprintRay ROI Calculator.
 *
 * Formula confirmed against the reference prototype (simuprint.vercel.app,
 * "Berechnungsmethode" panel), which literally states:
 *
 *   Savings        = Outsourcing − (Material + Design)
 *   ROI (months)   = Device price ÷ Savings/month
 *
 * ...and against its live results (e.g. lab 130€, material 25€, design 12€,
 * 8 cases/month → savings/case 93€ → 744€/month for that row). Net Profit
 * (Year 1) is Annual Savings minus the device price paid — confirmed by the
 * prototype's own breakdown text ("Einsparungen Jahr 1 − Ökosystempreis").
 * Months for ROI rounds UP (a case is only "paid off" once the full month's
 * savings have accrued), also confirmed against the prototype's displayed
 * value.
 */

/**
 * Aggregates savings across all active indications and derives the
 * headline results.
 *
 * @param {Object} input
 * @param {Array}  input.indications                    Active indications (id, defaultCasesPerMonth, defaultLabOutsourcingCost, defaultMaterialCost, defaultDesignCost).
 * @param {Object} input.casesPerMonthByIndication      User-edited cases/month, keyed by indication id.
 * @param {Object} input.labOutsourcingCostByIndication User-edited lab outsourcing cost/case, keyed by indication id.
 * @param {Object} input.materialCostByIndication       User-edited material cost/case, keyed by indication id.
 * @param {Object} input.designCostByIndication         User-edited design cost/case, keyed by indication id.
 * @param {number} input.devicePricePaid                Price actually paid for the selected device/ecosystem.
 * @return {Object} { perIndication, monthlySavings, annualSavings, netProfitYearOne, monthsForRoi }
 */
export const calculateResults = ( {
	indications,
	casesPerMonthByIndication,
	labOutsourcingCostByIndication,
	materialCostByIndication,
	designCostByIndication,
	devicePricePaid,
} ) => {
	const perIndication = indications.map( ( indication ) => {
		const casesPerMonth =
			casesPerMonthByIndication[ indication.id ] ??
			indication.defaultCasesPerMonth ??
			0;
		const labOutsourcingCost =
			labOutsourcingCostByIndication[ indication.id ] ??
			indication.defaultLabOutsourcingCost ??
			0;
		const materialCost =
			materialCostByIndication[ indication.id ] ??
			indication.defaultMaterialCost ??
			0;
		const designCost =
			designCostByIndication[ indication.id ] ??
			indication.defaultDesignCost ??
			0;
		const savingsPerCase =
			labOutsourcingCost - ( materialCost + designCost );
		const monthlySavings = savingsPerCase * casesPerMonth;

		return {
			indicationId: indication.id,
			casesPerMonth,
			labOutsourcingCost,
			materialCost,
			designCost,
			savingsPerCase,
			monthlySavings,
		};
	} );

	const monthlySavings = perIndication.reduce(
		( sum, row ) => sum + row.monthlySavings,
		0
	);
	const annualSavings = monthlySavings * 12;
	const netProfitYearOne = annualSavings - devicePricePaid;
	// Round up: the device only counts as "paid off" once a full month's
	// savings have accrued, matching the reference tool's displayed value.
	const monthsForRoi =
		monthlySavings > 0
			? Math.ceil( devicePricePaid / monthlySavings )
			: null;
	// The reference tool's "Jahreskosten — Labor vs. intern" bar chart:
	// what active indications would cost per year outsourced vs. produced
	// in-house (the difference is annualSavings, by construction).
	const annualLabCost = perIndication.reduce(
		( sum, row ) => sum + row.labOutsourcingCost * row.casesPerMonth * 12,
		0
	);
	const annualInternalCost = perIndication.reduce(
		( sum, row ) =>
			sum +
			( row.materialCost + row.designCost ) * row.casesPerMonth * 12,
		0
	);
	// The reference tool's "Amortisationskurve" chart: cumulative savings
	// minus the device price, starting negative and crossing zero at
	// `monthsForRoi`.
	const cumulativeSavings = Array.from( { length: 37 }, ( _, month ) => ( {
		month,
		cumulative: monthlySavings * month - devicePricePaid,
	} ) );

	return {
		perIndication,
		monthlySavings,
		annualSavings,
		netProfitYearOne,
		monthsForRoi,
		annualLabCost,
		annualInternalCost,
		cumulativeSavings,
	};
};

/**
 * Computes a monthly lease/loan payment.
 *
 * With no interest this is straight-line financing (principal ÷ term),
 * which is the reference prototype's default (interest rate left blank ->
 * 19000 ÷ 60 = 316.66... -> displayed as "317 €/mois"). With a non-zero
 * annual interest rate it falls back to the standard annuity payment
 * formula so the field isn't a dead end if a real financing offer includes
 * interest.
 *
 * @param {number} principal         Amount financed (device/solution price).
 * @param {number} termMonths        Lease/loan term in months.
 * @param {number} annualRatePercent Annual interest rate, e.g. 5 for 5%. 0 = straight-line.
 * @return {number} Monthly payment.
 */
export const calculateMonthlyRate = (
	principal,
	termMonths,
	annualRatePercent = 0
) => {
	if ( ! termMonths || termMonths <= 0 ) {
		return 0;
	}

	if ( ! annualRatePercent ) {
		return principal / termMonths;
	}

	const monthlyRate = annualRatePercent / 100 / 12;
	const factor = ( 1 + monthlyRate ) ** termMonths;

	return ( principal * monthlyRate * factor ) / ( factor - 1 );
};

/**
 * ROI calculation logic for the Labor (laboratory) calculator variant —
 * confirmed against the reference prototype's second mode
 * (simuprint.vercel.app/labo, "Berechnungsmethode" panel), which states:
 *
 *   Bruttoertrag = Abrechnungspreis − Materialkosten
 *   Cashflow     = Bruttoertrag − monatliche Leasingrate
 *
 * Unlike the Praxis formula, there is no design cost and no payoff-date
 * concept — the headline number is a recurring monthly net cashflow after
 * the lease payment, confirmed against the prototype's own default values
 * (Pro 2 solution 19000€, 60-month term, 0% interest -> 317€/month rate;
 * Nachtschiene row: 90€ billed − 3.6€ material = 86.4€/case × 4 cases/month
 * = 345.6€ -> rounds to the prototype's displayed 346€ gross/month for
 * that row).
 *
 * `billingPriceByIndication` reads the *same* `defaultLabOutsourcingCost`
 * field the Praxis formula reads as "lab outsourcing cost" — the reference
 * tool reuses one number for both meanings (see Settings.php).
 *
 * @param {Object}  input
 * @param {Array}   input.indications               Active indications (id, defaultCasesPerMonth, defaultLabOutsourcingCost, defaultMaterialCost).
 * @param {Object}  input.casesPerMonthByIndication User-edited cases/month, keyed by indication id.
 * @param {Object}  input.billingPriceByIndication  User-edited billing price/case, keyed by indication id.
 * @param {Object}  input.materialCostByIndication  User-edited material cost/case, keyed by indication id.
 * @param {number}  input.devicePricePaid           Price actually paid for the solution.
 * @param {number}  input.leaseTermMonths           Lease term in months.
 * @param {number}  input.annualInterestRate        Annual interest rate percent, 0 = straight-line.
 * @param {?number} input.contractualRateOverride   User-entered actual lease rate, if it differs from the calculated one.
 * @return {Object} { perIndication, monthlyGrossProfit, calculatedMonthlyRate, effectiveMonthlyRate, netCashflowMonthly, annualNetCashflow, cumulativeCashflow }
 */
/**
 * "Chair time freed" optional layer (Praxis only) — confirmed against the
 * reference prototype's "Optional layers" panel. Midas delivers a crown in
 * one appointment instead of two, freeing a chair slot per case; this layer
 * lets the user price that freed slot as new-patient revenue, plus the
 * material/assistant-time cost of provisionals no longer needed. Explicitly
 * excluded from the headline ROI figure — the reference tool labels it
 * "Additional potential ... Not included in the main ROI figure."
 *
 * Confirmed against the prototype's live defaults: 22 freed slots (all 5
 * Midas indications' cases/month), 80% target conversion, 150€ replacement
 * act fee -> 22 x 0.8 x 150 = 2,640€/month; plus 8 avoided provisionals x
 * 25€ = 200€/month -> (2,640 + 200) x 12 = 34,080€/year. Disabling one Midas
 * row (8 cases/month) drops freed slots to 14 -> (1,680 + 200) x 12 =
 * 22,560€/year — also confirmed live.
 *
 * @param {Object} input
 * @param {number} input.freedSlotsPerMonth              Sum of cases/month across active Midas-group indications.
 * @param {number} input.replacementActFee               Average revenue per new Midas patient.
 * @param {number} input.targetConversionRatePercent     Share of freed slots converted to new acts, e.g. 80 for 80%.
 * @param {number} input.provisionalCasesAvoidedPerMonth Provisional cases avoided per month.
 * @param {number} input.savingPerProvisional            Material + assistant time saved per avoided provisional.
 * @return {Object} { chairTimeMonthly, provisionalsMonthly, monthlyPotential, annualPotential }
 */
export const calculateChairTimeLayer = ( {
	freedSlotsPerMonth,
	replacementActFee,
	targetConversionRatePercent,
	provisionalCasesAvoidedPerMonth,
	savingPerProvisional,
} ) => {
	const chairTimeMonthly =
		freedSlotsPerMonth *
		( targetConversionRatePercent / 100 ) *
		replacementActFee;
	const provisionalsMonthly =
		provisionalCasesAvoidedPerMonth * savingPerProvisional;
	const monthlyPotential = chairTimeMonthly + provisionalsMonthly;

	return {
		chairTimeMonthly,
		provisionalsMonthly,
		monthlyPotential,
		annualPotential: monthlyPotential * 12,
	};
};

/**
 * "Financing (lease / LOA)" optional layer (Praxis only) — reframes the ROI
 * as a monthly cash-flow comparison instead of an upfront price comparison,
 * confirmed against the reference prototype's "Optional layers" panel. Reuses
 * `calculateMonthlyRate` above (the same formula the Labor calculator's
 * mandatory leasing step already uses) rather than introducing a second
 * financing formula.
 *
 * Confirmed against the prototype's live defaults: 5,589€/month savings,
 * 567€/month calculated rate (34,000€ over 60 months, 0% interest) ->
 * 5,589 - 567 = +5,022€/month net cash flow.
 *
 * @param {Object}  input
 * @param {number}  input.monthlySavings          The main calculator's monthlySavings (calculateResults' output).
 * @param {number}  input.calculatedMonthlyRate   calculateMonthlyRate()'s output for the device price/term/rate.
 * @param {?number} input.contractualRateOverride User-entered actual lease rate, if it differs from the calculated one.
 * @return {Object} { effectiveMonthlyRate, netMonthlyCashFlow }
 */
export const calculateFinancingLayer = ( {
	monthlySavings,
	calculatedMonthlyRate,
	contractualRateOverride,
} ) => {
	const effectiveMonthlyRate =
		contractualRateOverride ?? calculatedMonthlyRate;

	return {
		effectiveMonthlyRate,
		netMonthlyCashFlow: monthlySavings - effectiveMonthlyRate,
	};
};

/**
 * "Intraoral camera" optional layer (Praxis only) — adds a second piece of
 * equipment's monthly financing cost on top of the printer's, showing the
 * adjusted (longer) payback period, confirmed against the reference
 * prototype's "Optional layers" panel.
 *
 * Confirmed against the prototype live: with the base calculator at 7
 * months for ROI (34,000€ device / 5,589€/month), a 0€ or 100€/month camera
 * payment still rounds to 7 months (34,000 / (5,589-100) = 6.19 -> ceil 7);
 * a 3,000€/month payment gives 34,000 / (5,589-3,000) = 13.13 -> ceil 14
 * months, with the prototype additionally showing "Adds +7 months to ROI".
 *
 * @param {Object}  input
 * @param {number}  input.devicePricePaid      Price actually paid for the selected device/ecosystem.
 * @param {number}  input.monthlySavings       The main calculator's monthlySavings (calculateResults' output).
 * @param {?number} input.monthsForRoi         The main calculator's monthsForRoi, for the "+N months" delta.
 * @param {number}  input.cameraMonthlyPayment Monthly payment for the intraoral camera.
 * @return {Object} { adjustedRoiMonths, addedMonths }
 */
export const calculateCameraLayer = ( {
	devicePricePaid,
	monthlySavings,
	monthsForRoi,
	cameraMonthlyPayment,
} ) => {
	const adjustedMonthlySavings = monthlySavings - cameraMonthlyPayment;
	const adjustedRoiMonths =
		adjustedMonthlySavings > 0
			? Math.ceil( devicePricePaid / adjustedMonthlySavings )
			: null;
	const addedMonths =
		adjustedRoiMonths !== null && monthsForRoi !== null
			? adjustedRoiMonths - monthsForRoi
			: null;

	return { adjustedRoiMonths, addedMonths };
};

export const calculateLaborResults = ( {
	indications,
	casesPerMonthByIndication,
	billingPriceByIndication,
	materialCostByIndication,
	devicePricePaid,
	leaseTermMonths,
	annualInterestRate,
	contractualRateOverride,
} ) => {
	const perIndication = indications.map( ( indication ) => {
		const casesPerMonth =
			casesPerMonthByIndication[ indication.id ] ??
			indication.defaultCasesPerMonth ??
			0;
		const billingPrice =
			billingPriceByIndication[ indication.id ] ??
			indication.defaultLabOutsourcingCost ??
			0;
		const materialCost =
			materialCostByIndication[ indication.id ] ??
			indication.defaultMaterialCost ??
			0;
		const grossProfitPerCase = billingPrice - materialCost;
		const monthlyGrossProfit = grossProfitPerCase * casesPerMonth;

		return {
			indicationId: indication.id,
			casesPerMonth,
			billingPrice,
			materialCost,
			grossProfitPerCase,
			monthlyGrossProfit,
		};
	} );

	const monthlyGrossProfit = perIndication.reduce(
		( sum, row ) => sum + row.monthlyGrossProfit,
		0
	);
	const calculatedMonthlyRate = calculateMonthlyRate(
		devicePricePaid,
		leaseTermMonths,
		annualInterestRate
	);
	const effectiveMonthlyRate =
		contractualRateOverride ?? calculatedMonthlyRate;
	const netCashflowMonthly = monthlyGrossProfit - effectiveMonthlyRate;
	const annualNetCashflow = netCashflowMonthly * 12;
	// Chart data for a "cumulative cashflow" view up to the reference
	// prototype's 36-month axis. The device is financed via the lease
	// itself (no upfront lump sum to net out), so cumulative cashflow is
	// simply the monthly net cashflow accruing from month 0.
	const cumulativeCashflow = Array.from( { length: 37 }, ( _, month ) => ( {
		month,
		cumulative: netCashflowMonthly * month,
	} ) );

	return {
		perIndication,
		monthlyGrossProfit,
		calculatedMonthlyRate,
		effectiveMonthlyRate,
		netCashflowMonthly,
		annualNetCashflow,
		cumulativeCashflow,
	};
};
